import { connect } from 'react-redux'
import styled, { useTheme } from 'styled-components'

import { hasActiveSubscription } from '@helpers'

import { PortalProvider } from '@contexts/PortalProvider'

import { useCompactNavbarVisible } from '@hooks'

import { ServiceTypeShortLogo } from '@components/logos'
import { AppBar, AttentionIcon, FlexRow } from '@components/ui'
import { HamburgerMenuIcon } from '@components/ui/svgIcons'

import { COMPACT_NAVBAR_ID, ROUTES } from '@constants'

import type { DateFilterPageConfig, PageLink } from './elements'
import { DropdownMenuButton, NavLink, PageDateFilter, PageMenu, PageTabLink } from './elements'
import { CompactPersonalDropdownMenu } from './elements/CompactPersonalDropdownMenu'
import { MainMenu } from './PrivateNavbar/elements'

// need to use mui appbar for preventing jumping on popover's scroll lock behavior
// this component listens on it and makes correction with padding
const CompactNavbarWrapper = styled(AppBar).attrs({
  border: 'bottom',
  noShadow: true,
})`
  height: ${({ theme }) => theme.appBarHeightMedium}px;
  z-index: 1297;
`

const CompactNavbarHeader = styled.div`
  height: ${({ theme }) => `${theme.appBarHeightMedium}px`};
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`

const MainDropdownMenuButton = styled(DropdownMenuButton)`
  border-right: 1px solid ${({ theme }) => theme.colors.common.paperStroke};
  border-radius: unset;
`

const StyledPageMenu = styled(PageMenu)`
  padding: 0 10px;

  ${PageTabLink} {
    height: ${({ theme }) => `${theme.appBarHeightMedium}px`};
    padding: ${({ theme }) =>
      `calc((${theme.appBarHeightMedium}px - ${theme.typography['400-md'].lineHeight}) / 2) 10px`};
  }
  ${AttentionIcon} {
    top: 3px;
    right: 0;
  }
`

const StyledNavLink = styled(NavLink)`
  width: ${({ theme }) => `${theme.appBarHeightMedium}px`};
  height: ${({ theme }) => `${theme.appBarHeightMedium}px`};
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid ${({ theme }) => theme.colors.common.paperStroke};
`

interface CompactNavbarProps {
  dateFilterConfig?: DateFilterPageConfig
  hasSubscription: boolean
  isMaintenanceMode: boolean
  links: PageLink[]
}

function PureCompactNavbar({ dateFilterConfig, hasSubscription, isMaintenanceMode, links }: CompactNavbarProps) {
  const visible = useCompactNavbarVisible()
  const theme = useTheme()

  return (
    <CompactNavbarWrapper
      id={COMPACT_NAVBAR_ID}
      style={{
        '--top': visible ? '0px' : `-${theme.appBarHeightMedium}px`,
      }}
      aria-hidden={!visible}
    >
      <PortalProvider>
        <CompactNavbarHeader>
          <FlexRow>
            {isMaintenanceMode && <ServiceTypeShortLogo />}
            {!isMaintenanceMode && (
              <>
                <StyledNavLink to={ROUTES.root}>
                  <ServiceTypeShortLogo />
                </StyledNavLink>
                <MainDropdownMenuButton
                  icon={<HamburgerMenuIcon />}
                  isContainerVisible={visible}
                  placement="bottom-start"
                  transformOrigin="left top"
                  width={240}
                >
                  {() => <MainMenu hasSubscription={hasSubscription} />}
                </MainDropdownMenuButton>
                <StyledPageMenu links={links} />
              </>
            )}
          </FlexRow>
          <FlexRow>
            {dateFilterConfig && (
              <PageDateFilter
                dateTypeOptions={dateFilterConfig.dateTypeOptions}
                disabled={dateFilterConfig.disabled}
                getDateTypeValue={dateFilterConfig.getDateTypeValue}
                isDateFilterToggleDisabled={dateFilterConfig.isDateFilterToggleDisabled}
                onChange={dateFilterConfig.onChange}
                onToggle={dateFilterConfig.onToggle}
                variant="compact"
              />
            )}
            <CompactPersonalDropdownMenu visible={visible} />
          </FlexRow>
        </CompactNavbarHeader>
      </PortalProvider>
    </CompactNavbarWrapper>
  )
}

export const CompactNavbar = connect((state: Store) => ({
  hasSubscription: hasActiveSubscription(state),
  isMaintenanceMode: state.auth.company.isMaintenanceMode,
}))(PureCompactNavbar)

CompactNavbar.displayName = 'CompactNavbar'
